import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Zap, Calculator, AlertCircle, Download } from "lucide-react";
import api from "@/lib/api";

const fmt = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

export default function ElectricalModule() {
  const { i18n } = useTranslation();
  const es = i18n.language?.startsWith("es");
  const [templates, setTemplates] = useState([]);
  const [selectedTpl, setSelectedTpl] = useState("commercial_office");
  const [sqft, setSqft] = useState(5000);
  const [markup, setMarkup] = useState(18);
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/modules/electrical/templates").then(r => setTemplates(r.data.templates || []));
  }, []);

  const run = async () => {
    setError(null); setLoading(true);
    try {
      const { data } = await api.post("/modules/electrical/quick-estimate", {
        template: selectedTpl, square_feet: Number(sqft), markup_pct: Number(markup),
      });
      setEstimate(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const currentTemplate = templates.find(t => t.key === selectedTpl);

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="electrical-module-page">
      <header className="flex items-start justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-amber-600">
            <Zap size={13} /> {es ? "MÓDULO ELÉCTRICO · NEC 2026" : "ELECTRICAL MODULE · NEC 2026"}
          </div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900 mt-1">
            {es ? "Estimador Rápido Eléctrico" : "Quick Electrical Estimator"}
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            {es
              ? "Genera un BOM preliminar en segundos a partir de un template de ocupación y los pies cuadrados. Luego ajusta con planos y site survey."
              : "Generate a preliminary BOM in seconds from an occupancy template + square footage. Refine later with drawings and site survey."}
          </p>
        </div>
      </header>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Left: form */}
        <div className="lg:col-span-1">
          <div className="iqes-card p-6 sticky top-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <Calculator size={16} /> {es ? "Parámetros" : "Parameters"}
            </h3>

            <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1.5">
              {es ? "Tipo de ocupación" : "Occupancy template"}
            </label>
            <select
              data-testid="electrical-template-select"
              value={selectedTpl}
              onChange={e => setSelectedTpl(e.target.value)}
              className="iqes-input mb-4"
            >
              {templates.map(t => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>

            <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1.5">
              {es ? "Pies cuadrados" : "Square feet"}
            </label>
            <input
              data-testid="electrical-sqft-input"
              type="number" min="100" step="100"
              value={sqft}
              onChange={e => setSqft(e.target.value)}
              className="iqes-input mb-4 font-mono"
            />

            <label className="text-xs font-mono uppercase tracking-wider text-slate-500 block mb-1.5">
              {es ? "Markup %" : "Markup %"}
            </label>
            <input
              data-testid="electrical-markup-input"
              type="number" min="0" max="100" step="1"
              value={markup}
              onChange={e => setMarkup(e.target.value)}
              className="iqes-input mb-5 font-mono"
            />

            <button
              data-testid="electrical-run-btn"
              onClick={run}
              disabled={loading}
              className="w-full bg-[#003366] text-white hover:bg-[#002244] rounded-xl px-4 py-2.5 font-medium text-sm transition-colors disabled:opacity-60 shadow-sm"
            >
              {loading ? (es ? "Calculando…" : "Calculating…") : (es ? "Generar estimación" : "Generate estimate")}
            </button>

            {currentTemplate && (
              <div className="mt-5 pt-5 border-t border-slate-100">
                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
                  {es ? "Descripción" : "Description"}
                </div>
                <p className="text-xs text-slate-600 leading-relaxed mb-4">{currentTemplate.description}</p>

                <div className="text-xs font-mono uppercase tracking-wider text-slate-500 mb-2">
                  {es ? "Referencias NEC" : "NEC references"}
                </div>
                <ul className="space-y-1">
                  {currentTemplate.nec_refs?.map((r, i) => (
                    <li key={i} className="text-xs text-slate-700 font-mono flex gap-1">
                      <span className="text-amber-600">§</span> {r}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>

        {/* Right: result */}
        <div className="lg:col-span-2">
          {error && (
            <div className="iqes-card p-4 mb-4 flex items-start gap-2 text-sm text-red-700 bg-red-50 border-red-200">
              <AlertCircle size={16} className="mt-0.5" /> {error}
            </div>
          )}

          {!estimate ? (
            <div className="iqes-card p-12 text-center">
              <Zap size={32} className="mx-auto text-slate-300 mb-3" />
              <p className="text-slate-500 text-sm">
                {es
                  ? "Selecciona un template y genera tu primera estimación."
                  : "Pick a template and run your first estimate."}
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="iqes-card p-4">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Material</div>
                  <div className="text-xl font-mono font-semibold text-slate-900 mt-1">
                    {fmt.format(estimate.totals.material_total)}
                  </div>
                </div>
                <div className="iqes-card p-4">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Labor</div>
                  <div className="text-xl font-mono font-semibold text-slate-900 mt-1">
                    {fmt.format(estimate.totals.labor_total)}
                  </div>
                </div>
                <div className="iqes-card p-4 bg-[#003366] text-white border-[#003366]">
                  <div className="text-[10px] font-mono uppercase tracking-wider text-[#2DD4BF]">
                    {es ? "Total c/Markup " : "Grand Total "}{estimate.totals.markup_pct}%
                  </div>
                  <div className="text-xl font-mono font-semibold mt-1">
                    {fmt.format(estimate.totals.grand_total)}
                  </div>
                </div>
              </div>

              <div className="iqes-card overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-slate-100">
                  <h3 className="font-semibold text-slate-900">
                    {es ? "Bill of Materials" : "Bill of Materials"}
                    <span className="ml-2 text-xs font-mono text-slate-500">
                      ({estimate.bom.length} {es ? "items" : "items"})
                    </span>
                  </h3>
                  <button
                    data-testid="electrical-export-btn"
                    onClick={() => {
                      const csv = ["Code,Label,Qty,Unit,Material,Labor,Subtotal,Note"];
                      estimate.bom.forEach(r => csv.push(
                        `${r.code},"${r.label}",${r.qty},${r.unit},${r.material},${r.labor},${r.subtotal},"${r.note || ""}"`
                      ));
                      const blob = new Blob([csv.join("\n")], { type: "text/csv" });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement("a"); a.href = url;
                      a.download = `electrical-${estimate.template}-${estimate.square_feet}sqft.csv`;
                      a.click(); URL.revokeObjectURL(url);
                    }}
                    className="text-xs font-mono uppercase tracking-wider text-[#003366] hover:underline flex items-center gap-1"
                  >
                    <Download size={12} /> CSV
                  </button>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-xs font-mono uppercase tracking-wider text-slate-500">
                      <th className="text-left p-3">{es ? "Item" : "Item"}</th>
                      <th className="text-right p-3">Qty</th>
                      <th className="text-right p-3">Mat</th>
                      <th className="text-right p-3">Labor</th>
                      <th className="text-right p-3">Sub</th>
                    </tr>
                  </thead>
                  <tbody data-testid="electrical-bom-table">
                    {estimate.bom.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="p-3">
                          <div className="font-medium text-slate-900">{r.label}</div>
                          {r.note && <div className="text-[11px] text-slate-500 mt-0.5">{r.note}</div>}
                        </td>
                        <td className="p-3 text-right font-mono text-slate-700">{r.qty} {r.unit}</td>
                        <td className="p-3 text-right font-mono text-slate-700">{fmt.format(r.material)}</td>
                        <td className="p-3 text-right font-mono text-slate-700">{fmt.format(r.labor)}</td>
                        <td className="p-3 text-right font-mono font-semibold text-slate-900">{fmt.format(r.subtotal)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="iqes-card-soft mt-4 p-4 bg-amber-50/50 border-amber-100">
                <div className="text-[11px] font-mono uppercase tracking-wider text-amber-700 mb-1">
                  {es ? "Nota" : "Note"}
                </div>
                <p className="text-xs text-amber-900 leading-relaxed">{estimate.note}</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
